<template>
  <div>
    <property-report-card
      title="Market Rents"
      description="View average rents by unit type based on recent listings."
      :printing-mode="printingMode"
    >
      <template #actions>
        <q-btn
          v-if="unitsToExclude.length > 0"
          no-caps
          flat
          icon="delete"
          label="Remove Units"
          @click="removeUnit"
        />
        <q-btn flat no-caps @click="exportCSV">
          <q-icon name="download" class="q-mr-sm" size="medium" />Download
        </q-btn>
        <q-btn icon="settings" no-caps label="Customize" flat @click="openSettingModal" />
      </template>
      <q-markup-table v-if="isLoading(pricings)" bordered dense>
        <thead>
          <tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <th v-for="n in 13" scope="col" :key="n">
              <q-skeleton animation="blink" type="text" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="n in 20" :key="n">
            <td v-for="c in 13" :key="c">
              <q-skeleton animation="blink" type="text" />
            </td>
          </tr>
        </tbody>
      </q-markup-table>
      <q-table
        v-else
        id="intercom-price-recommendation-content"
        row-key="id"
        bordered
        dense
        color="primary"
        flat
        :rows="rows"
        :columns="columns"
        :pagination="{
          rowsPerPage: 20
        }"
        :hide-pagination="printingMode"
      >
        <template #header="props">
          <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <q-th auto-width />
            <q-th
              v-for="col in props.cols"
              :key="col.name"
              :props="props"
              @click="sortingChanged(col.name)"
            >
              {{ col.label }}
              <q-tooltip v-if="col.description" max-width="250px">
                <b>{{ col.label }}</b>
                <br />
                {{ col.description }}
              </q-tooltip>
            </q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr :props="props" :class="{ 'bg-deep-purple-light text-bold': props.expand }">
            <q-td auto-width>
              <q-btn
                v-if="!printingMode"
                unelevated
                dense
                size="xs"
                :icon="props.expand ? 'expand_less' : 'expand_more'"
                @click="
                  () => {
                    props.expand = !props.expand
                    if (props.expand) $tracker.openedPopup('price_recommendation_details')
                  }
                "
              />
            </q-td>
            <q-td
              v-for="col in props.cols"
              :key="col.name"
              :props="props"
              :class="{
                'text-bold text-primary':
                  col.name === 'recommendedPrice' || col.name === 'recommendedPricePSF'
              }"
            >
              <div v-if="col.name === 'bed'" :class="`hd-${props.row.bed}-bed-bg hd-badge`">
                {{ col.value }}
              </div>
              <template v-else>
                {{ col.value }}
              </template>
            </q-td>
          </q-tr>
          <tr v-if="props.expand">
            <td
              colspan="100%"
              style="
                position: absolute;
                left: -15px;
                right: -15px;
                z-index: 1;
                padding: 0;
                margin-top: -1px;
              "
            >
              <q-card flat bordered>
                <q-card-section class="flex justify-between">
                  <div>
                    <div>Supporting Units ({{ props.row.supportingData.length }})</div>
                    <div class="text-caption text-grey">
                      These are the units that are used to compute the recommended rent for the
                      unit.
                    </div>
                  </div>
                  <div class="q-gutter-x-md">
                    <q-btn
                      v-if="
                        compsSettings.excluded_unit_ids &&
                        compsSettings.excluded_unit_ids?.length > 0
                      "
                      no-caps
                      flat
                      dense
                      icon="refresh"
                      :label="`Reset Excluded Units (${compsSettings.excluded_unit_ids.length})`"
                      @click="resetExcludeUnits"
                    />
                    <q-btn
                      no-caps
                      flat
                      dense
                      outline
                      icon="close"
                      label="Close"
                      @click="props.expand = false"
                    />
                  </div>
                </q-card-section>
                <q-separator />
                <q-markup-table flat dense>
                  <thead>
                    <tr>
                      <th scope="col" style="text-align: left">Property Name</th>
                      <th scope="col" style="text-align: left">Floorplan</th>
                      <th scope="col" style="text-align: left">Unit</th>
                      <th scope="col" style="text-align: right">Type</th>
                      <th scope="col" style="text-align: right">SF</th>
                      <th scope="col" style="text-align: right">Days on Mkt</th>
                      <th scope="col" style="text-align: right">Available On</th>
                      <th scope="col" style="text-align: right">Leased Date</th>
                      <th scope="col" style="text-align: right">Similarity</th>
                      <th scope="col" style="text-align: right">Last Ask Rent</th>
                      <th scope="col" style="text-align: right">Last NER</th>
                      <th scope="col" style="text-align: right">Last Ask Rent PSF</th>
                      <th scope="col" style="text-align: right">Last NER PSF</th>
                      <th scope="col" style="text-align: right">Exclude</th>
                    </tr>
                  </thead>
                  <tbody>
                    <q-tr v-for="row in props.row.supportingData" :key="row.unit.id">
                      <q-td class="constrained-cell">
                        {{ row.unit.building_name || row.unit.street_address }}
                      </q-td>
                      <q-td class="constrained-cell">
                        {{ row.unit.floorplan_name }}
                      </q-td>
                      <q-td class="constrained-cell">
                        {{ row.unit.unit_name }}
                      </q-td>
                      <q-td style="text-align: right">
                        <div :class="`hd-${row.unit.bed}-bed-bg hd-badge`">
                          {{
                            `${row.unit.bed}/${row.unit.bath}${row.unit.partial_bath ? '.5' : ''}`
                          }}
                        </div>
                      </q-td>
                      <q-td style="text-align: right">
                        {{ formatNumber(row.unit.sqft) }}
                      </q-td>
                      <q-td key="daysOnMarket" :props="props">
                        {{ formatNumber(row.daysOnMarket) }}
                      </q-td>
                      <q-td style="text-align: right">
                        {{
                          row.unit.availability
                            ? format(parseISO(row.unit.availability), 'M/d/yyyy')
                            : '-'
                        }}
                      </q-td>
                      <q-td style="text-align: right">
                        {{
                          row.unit.exit_market
                            ? format(parseISO(row.unit.exit_market), 'M/d/yyyy')
                            : '-'
                        }}
                      </q-td>
                      <q-td style="text-align: right">
                        <q-avatar
                          rounded
                          size="22px"
                          :style="{ backgroundColor: similarityColor(row.similarity) }"
                          text-color="white"
                        >
                          {{ (row.similarity * 100).toFixed(0) }}
                        </q-avatar>
                      </q-td>
                      <q-td style="text-align: right">
                        {{ formatMoney(row.unit.price) }}
                      </q-td>
                      <q-td style="text-align: right">
                        {{ formatMoney(row.unit.effective_price) }}
                      </q-td>
                      <q-td style="text-align: right">
                        {{
                          row.unit.price !== null && row.unit.sqft !== null
                            ? formatMoney(row.unit.price / row.unit.sqft, 2)
                            : ''
                        }}
                      </q-td>
                      <q-td style="text-align: right">
                        {{
                          row.unit.effective_price !== null && row.unit.sqft !== null
                            ? formatMoney(row.unit.effective_price / row.unit.sqft, 2)
                            : ''
                        }}
                      </q-td>
                      <q-td style="text-align: right">
                        <q-checkbox v-model="unitsToExclude" :val="row.unit.id" dense />
                      </q-td>
                    </q-tr>
                  </tbody>
                </q-markup-table>
                <q-separator />
                <q-card-section class="flex justify-between">
                  <q-btn
                    no-caps
                    flat
                    dense
                    outline
                    icon="close"
                    label="Close"
                    @click="props.expand = false"
                  />
                </q-card-section>
              </q-card>
            </td>
          </tr>
        </template>
        <template #no-data>
          <div class="text-center full-width q-ma-md" style="height: 200px">
            <template v-if="!subjectDetail"> Subject Property Required </template>
            <template v-else-if="isLoading(pricings) || isNotStarted(pricings)">
              <div class="text-center full-width q-ma-md" style="height: 200px">
                <q-inner-loading />
                Loading...
              </div>
            </template>
            <template v-else-if="isError(pricings)">
              <q-icon name="error" class="text-red-5" />
              Error loading data. Please try again.
            </template>
            <template v-else>
              No active listings with sqft data or recent closings found.
            </template>
          </div>
        </template>
      </q-table>
    </property-report-card>
    <q-dialog v-if="showSettingModal" v-model="showSettingModal" transition-duration="100">
      <card-pricing-settings
        :comps-settings="compsSettings"
        :comps-ids="compsIds"
        @update:comps-settings="updatedCompsSettings"
      />
    </q-dialog>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { type QTableColumn, exportFile } from 'quasar'
import { parseISO, format, addDays } from 'date-fns'

import {
  type PropertyDetailsResponse,
  type PricingResponse,
  type PricingSettings,
  ListingStatus
} from 'common'
import {
  formatMoney,
  formatNumber,
  formatFloorplanName,
  unitTypeColor,
  formatPercent,
  similarityColor
} from '@/utils'
import CardPricingSettings from '@/components/CardPricingSettings.vue'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { isError, isLoaded, isLoading, isNotStarted } from '@/utils/loading'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

export default {
  name: 'PropertyComparePriceRecommendation',
  components: { PropertyReportCard, CardPricingSettings },
  props: {
    subjectDetail: {
      type: [Object, null] as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    compsIds: {
      type: Array as PropType<string[]>,
      required: true
    },
    unitTypes: {
      type: Object as PropType<{ bed: number; bath: number; partial_bath: number | null }[]>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      unitTypeColor,
      similarityColor,
      showSettingModal: false,
      unitsToExclude: [] as string[],
      columns: [
        {
          field: () => this.subjectDetail?.building_name || this.subjectDetail?.street_address,
          name: 'buildingName',
          label: 'Property Name',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          classes: 'constrained-cell',
          description: 'The name of the property the unit belongs to.'
        },
        {
          field: (row) => row.unitName,
          name: 'unitName',
          label: 'Unit',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          classes: 'constrained-cell',
          description: 'The name of the unit.'
        },
        {
          field: (row) => row.floorplanName,
          name: 'floorplanName',
          label: 'Floorplan',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          classes: 'constrained-cell',
          description: 'The name of the floorplan the unit belongs to.'
        },
        {
          field: (row) => {
            const { bed, bath, partial_bath } = row
            return `${bed}/${bath}${partial_bath ? '.5' : ''}`
          },
          name: 'bed',
          label: 'Type',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          description: 'The unit type, formated as #bed/#bath'
        },
        {
          field: (row) => row.sqft,
          name: 'sqft',
          label: 'SF',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          format: (_, row) => `${formatNumber(row.sqft)}`,
          description: 'The square footage of the unit.'
        },
        {
          field: (row) => row.daysOnMarket,
          name: 'daysOnMarket',
          label: 'Days on Mkt',
          sortable: true,
          align: 'right',
          sortOrder: 'da',
          format: (_, row) => `${formatNumber(row.daysOnMarket)}`,
          description: 'The number of days the unit was on the market before it was rented.'
        },
        {
          field: (row) => row.availability,
          name: 'availability',
          label: 'Available On',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          format: (val: string) => {
            return val ? format(parseISO(val), 'M/d/yyyy') : '-'
          },
          description: 'The date the unit will be available for rent.'
        },
        {
          field: (row) => row.price,
          name: 'price',
          label: 'Last Ask Rent',
          sortable: true,
          align: 'right',
          sortOrder: 'da',
          format: (val) => formatMoney(val, 0),
          description: 'The last asking rent of the unit.'
        },
        {
          field: (row) => row.effective_price,
          name: 'effective_price',
          label: 'Last NER',
          sortable: true,
          align: 'right',
          sortOrder: 'da',
          format: (val) => formatMoney(val, 0),
          description: 'The last effective rent of the unit.'
        },
        {
          name: 'rent_per_sqft',
          label: 'Last Asking Rent PSF',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          format: (_, row) => {
            if (row.price && row.sqft) {
              return formatMoney(row.price / row.sqft, 2)
            }
            return ''
          },
          description: 'The last asking rent per square foot of the unit.'
        },
        {
          name: 'effective_rent_per_sqft',
          label: 'Last NER PSF',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          format: (_, row) => {
            if (row.effective_price && row.sqft) {
              return formatMoney(row.effective_price / row.sqft, 2)
            }
            return ''
          },
          description: 'The last effective rent per square foot of the unit.'
        },
        {
          field: (row) => row.recommendedPrice,
          name: 'recommendedPrice',
          label: 'Recommended Rent',
          sortable: true,
          align: 'right',
          sortOrder: 'da',
          format: (_, row) => formatMoney(row.recommendedPrice, 0),
          description:
            'The recommended asking rent for the unit. It is computed as the weighted average of the supporting units, weighted by their similarity to the subject unit.'
        },
        {
          name: 'recommendedPricePSF',
          label: 'Recommended Rent PSF',
          sortable: true,
          align: 'right',
          sortOrder: 'da',
          field: (row) => {
            if (row.recommendedPrice && row.sqft) {
              return row.recommendedPrice / row.sqft
            }
            return null
          },
          format: (val) => {
            if (val) {
              return formatMoney(val, 2)
            }
            return ''
          },
          description:
            'The recommended asking rent per square foot for the unit. It is computed as the weighted average of the supporting units, weighted by their similarity to the subject unit.'
        }
      ] as QTableColumn[],
      compsSettings: {
        max_radius: null,
        renovations: null,
        listing_status: ListingStatus.ALL,
        from_date: format(addDays(new Date(), -30), 'yyyy-MM-dd'),
        to_date: format(new Date(), 'yyyy-MM-dd'),
        include_outliers: false,
        building_ids: this.compsIds,
        excluded_unit_ids: [],
        exclude_subject: false,
        use_embedding: false
      } as PricingSettings
    }
  },
  computed: {
    pricings() {
      return usePropertyReportStore().unitPricings
    },
    rows() {
      if (!isLoaded(this.pricings)) {
        return []
      }
      return this.pricings.data.flatMap((p: PricingResponse, idx: number) => {
        if (
          this.unitTypes.length > 0 &&
          !this.unitTypes.some(
            (x) =>
              x.bed === p.unit.bed &&
              x.bath === p.unit.bath &&
              x.partial_bath === p.unit.partial_bath
          )
        ) {
          return []
        }
        const lastDate = p.unit.exit_market ? parseISO(p.unit.exit_market) : new Date()
        const daysOnMarket = p.unit.enter_market
          ? Math.floor(
              (lastDate.getTime() - parseISO(p.unit.enter_market).getTime()) / (1000 * 3600 * 24)
            )
          : null
        return {
          id: idx,
          unitName: p.unit.unit_name,
          floorplanName: formatFloorplanName(p.unit.floorplan_name, p.unit.bed),
          bed: p.unit.bed,
          bath: p.unit.bath,
          sqft: p.unit.sqft,
          price: p.unit.price,
          availability: p.unit.availability,
          effective_price: p.unit.effective_price,
          daysOnMarket: daysOnMarket,
          recommendedPrice: p.predicted_price ? Math.round(p.predicted_price) : null,
          recommendedPricePerSqft: p.predicted_price_sqft,
          supportingData: p.supporting_units.map((x) => {
            // we  just add days on market to the supporting units
            const lastDate = x.unit.exit_market ? parseISO(x.unit.exit_market) : new Date()
            const daysOnMarket = x.unit.enter_market
              ? Math.floor(
                  (lastDate.getTime() - parseISO(x.unit.enter_market).getTime()) /
                    (1000 * 3600 * 24)
                )
              : null
            x.unit.floorplan_name = formatFloorplanName(x.unit.floorplan_name, x.unit.bed)
            return {
              ...x,
              daysOnMarket
            }
          })
        }
      })
    }
  },
  watch: {
    subjectDetail: {
      handler(newVal) {
        if (newVal && isNotStarted(this.pricings)) {
          usePropertyReportStore()
            .getOldRevenueManagement(newVal, this.compsSettings)
            .catch(console.error)
        }
      },
      immediate: true
    }
  },
  methods: {
    format,
    parseISO,
    formatMoney,
    formatNumber,
    formatPercent,
    isError,
    isLoaded,
    isNotStarted,
    isLoading,
    openSettingModal() {
      this.showSettingModal = true
    },
    exportCSV() {
      const exportDecimal = (num: number | null) => {
        return typeof num === 'number' ? Math.round(num * 100) / 100 : null
      }
      // return the table's content (without the expandable rows) as a CSV
      const columns = [
        {
          label: 'Property Name',
          valFn: () => this.subjectDetail?.building_name || this.subjectDetail?.street_address,
          formatFn: null
        },
        {
          label: 'Unit Name',
          valFn: (row: { unitName: string }) => row.unitName,
          formatFn: null
        },
        {
          label: 'Floorplan',
          valFn: (row: { floorplanName: string }) => row.floorplanName,
          formatFn: null
        },
        {
          label: 'Type',
          valFn: (row: { bed: number; bath: number; partial_bath: number | null }) => {
            return `${row.bed}/${row.bath}${row.partial_bath ? '.5' : ''}`
          },
          formatFn: null
        },
        {
          label: 'SF',
          valFn: (row: { sqft: number }) => row.sqft,
          formatFn: (val: number | null) => (val ? Math.round(val) : null)
        },
        {
          label: 'Days on Mkt',
          valFn: (row: { daysOnMarket: number }) => row.daysOnMarket,
          formatFn: (val: number | null) => (val ? Math.round(val) : null)
        },
        {
          label: 'Available On',
          valFn: (row: { availability: string }) => row.availability,
          formatFn: (val: string) => format(parseISO(val), 'M/d/yyyy')
        },
        {
          label: 'Last Ask Rent',
          valFn: (row: { price: number }) => row.price,
          formatFn: exportDecimal
        },
        {
          label: 'Last NER',
          valFn: (row: { effective_price: number }) => row.effective_price,
          formatFn: exportDecimal
        },
        {
          label: 'Last Asking Rent PSF',
          valFn: (row: { price: number; sqft: number }) => row.price / row.sqft,
          formatFn: exportDecimal
        },
        {
          label: 'Last NER PSF',
          valFn: (row: { effective_price: number; sqft: number }) => row.effective_price / row.sqft,
          formatFn: exportDecimal
        },
        {
          label: 'Recommended Rent',
          valFn: (row: { recommendedPrice: number }) => row.recommendedPrice,
          formatFn: exportDecimal
        },
        {
          label: 'Recommended Rent PSF',
          valFn: (row: { recommendedPrice: number; sqft: number }) =>
            row.recommendedPrice / row.sqft,
          formatFn: exportDecimal
        }
      ]

      const headers = columns.map((col) => col.label)

      const rows = this.rows.map((row) =>
        columns.map((col) => {
          const value = col.valFn(row)
          return col.formatFn ? col.formatFn(value) : value
        })
      )

      const today = new Date().toISOString().split('T')[0]
      exportFile(
        `hello-data-price-recommendation-${today}.csv`,
        headers.join(',') + '\n' + rows.map((row) => row.join(',')).join('\n')
      )

      this.$tracker.exported('price_recommendation')
    },
    async resetExcludeUnits() {
      const newCompsSettings = {
        ...this.compsSettings,
        excluded_unit_ids: []
      }
      this.unitsToExclude = []
      await this.updatedCompsSettings(newCompsSettings)
    },
    async removeUnit() {
      const newExcludedUnitIds = this.compsSettings.excluded_unit_ids
        ? [...this.compsSettings.excluded_unit_ids, ...this.unitsToExclude]
        : this.unitsToExclude
      const newCompsSettings = {
        ...this.compsSettings,
        excluded_unit_ids: newExcludedUnitIds
      }
      this.unitsToExclude = []
      await this.updatedCompsSettings(newCompsSettings)
    },
    async updatedCompsSettings(compsSettings: PricingSettings) {
      this.showSettingModal = false
      this.compsSettings = compsSettings
      if (this.subjectDetail) {
        await usePropertyReportStore().getOldRevenueManagement(
          this.subjectDetail,
          this.compsSettings
        )
      }
    },
    sortingChanged(colName: string) {
      this.$tracker.changedSort('price_recommendation', colName)
    }
  }
}
</script>

<style scoped>
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
